/**
 * Session-restore helpers: tell a real "unauthorized" apart from transient
 * failures, and retry transient ones with a short backoff.
 */

export function httpStatusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
}

export function isUnauthorizedError(error: unknown): boolean {
  return httpStatusOf(error) === 401;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Run `fn`; on a non-401 failure retry up to `attempts` times (backoff). */
export async function withSessionRetry<T>(fn: () => Promise<T>, attempts = 3, baseDelayMs = 700): Promise<T> {
  let lastError: unknown;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (isUnauthorizedError(error) || i === attempts - 1) break;
      await wait(baseDelayMs * (i + 1));
    }
  }
  throw lastError;
}
